// HM-311.r cluster 15 visual rework + cluster 15.3 micro-fix: KPI strip
// 5 cards 1fr ognuna. La card "Incasso oggi" e' stata rimossa (cluster
// 15.3): la visibilita' dell'incasso giornaliero richiede permessi
// granulari per ruolo (admin si, reception/housekeeping no) e verra'
// implementata nel Modulo 5 Cassa (cluster 18) con il vero endpoint
// payments aggregati. Layout: 5 cols xl, 3 cols lg, 2 cols base.

import {
  BedDouble,
  HM_ICON_STROKE_WIDTH,
  LogIn,
  LogOut,
  type LucideIcon,
  Sparkles,
  UtensilsCrossed,
} from '@hotel-manager/ui/icons';
import type { DashboardKpis } from './useDashboardKpi.js';

type TrendVariant = 'up' | 'down' | 'neutral' | 'warning';

interface KpiSpec {
  icon: LucideIcon;
  label: string;
  value: string;
  total?: string;
  trend: { variant: TrendVariant; text: string };
}

interface Props {
  kpis: DashboardKpis;
}

export function KpiStrip({ kpis }: Props) {
  const occupancyPct = kpis.totalRooms
    ? Math.round((kpis.occupiedRooms / kpis.totalRooms) * 100)
    : 0;

  const cards: KpiSpec[] = [
    {
      icon: BedDouble,
      label: 'Occupate',
      value: String(kpis.occupiedRooms),
      total: `/${kpis.totalRooms}`,
      trend: { variant: 'up', text: `${occupancyPct}% occup.` },
    },
    {
      icon: LogIn,
      label: 'Check-in oggi',
      value: String(kpis.arrivalsToday),
      trend: {
        variant: 'neutral',
        text: kpis.arrivalsArriving > 0 ? `${kpis.arrivalsArriving} in arrivo` : '—',
      },
    },
    {
      icon: LogOut,
      label: 'Check-out',
      value: String(kpis.departuresToday),
      trend: {
        variant: kpis.departuresExpiringSoon > 0 ? 'warning' : 'neutral',
        text:
          kpis.departuresExpiringSoon > 0 ? `${kpis.departuresExpiringSoon} in scadenza` : 'oggi',
      },
    },
    {
      icon: UtensilsCrossed,
      label: 'Ordini RS',
      value: String(kpis.pendingOrders),
      trend: {
        variant: kpis.ordersPreparing > 0 ? 'warning' : 'neutral',
        text: kpis.ordersPreparing > 0 ? `${kpis.ordersPreparing} in preparazione` : 'pending',
      },
    },
    {
      icon: Sparkles,
      label: 'Da pulire',
      value: String(kpis.roomsToClean),
      trend: { variant: 'neutral', text: 'prossime 2h' },
    },
  ];

  return (
    <ul
      data-testid="kpi-strip"
      // SPEC mockup v3 §kpi-strip: 5 cols 1fr (3 mobile/tablet, 2 base).
      // Card padding 18 + label 12/500 con icona 16 + value 32/700 +
      // total 16 + trend 11/500.
      // HM-600 iPad UX: gap + 5 colonne anche su tablet (<=1280) per strip
      // compatta in una riga; padding/font ridotti via max-xl.
      className="grid gap-3 max-xl:gap-2 grid-cols-2 sm:grid-cols-3 max-xl:grid-cols-5 xl:grid-cols-5 list-none p-0 m-0"
      aria-label="KPI dashboard"
    >
      {cards.map((c) => (
        <li
          key={c.label}
          className="bg-bg-card rounded-xl border border-border-base p-[18px] max-xl:p-3 transition-all duration-150 hover:-translate-y-px hover:shadow-hm-sm cursor-pointer"
        >
          <div className="flex items-center gap-1.5 text-text-secondary text-xs font-medium mb-2.5 max-xl:mb-1.5">
            <c.icon size={16} strokeWidth={HM_ICON_STROKE_WIDTH} className="text-accent-primary" />
            {c.label}
          </div>
          <div className="text-[32px] max-xl:text-2xl font-bold text-text-primary leading-none tracking-[-0.02em]">
            {c.value}
            {c.total && (
              <span className="text-base text-text-muted font-normal ml-0.5">{c.total}</span>
            )}
          </div>
          <div className={`text-[11px] mt-1.5 font-medium ${trendClass(c.trend.variant)}`}>
            {c.trend.text}
          </div>
        </li>
      ))}
    </ul>
  );
}

function trendClass(v: TrendVariant): string {
  switch (v) {
    case 'up':
      return 'text-[#5F8A50]';
    case 'down':
      return 'text-state-guas';
    case 'warning':
      return 'text-accent-primary-text';
    case 'neutral':
      return 'text-text-secondary';
  }
}
